<!-- eslint-disable curly -->
<template>
  <v-dialog
    v-model="modalState"
    width="950"
    persistent
    retain-focus
  >
    <v-card>
      <v-form ref="formAmnio" v-model="formValid">
        <v-card-title
          ref="amnioTitle"
          class="sticky-header"
        >
          Amniotic Tissue Graft
        </v-card-title>

        <AmnioMedicalNecessity
          v-model="amnios_data.reason_required"
          :wound-id="woundId"
          :encounter-visit-date="encounterVisitDate"
          :wound-current-area="woundCurrentArea"
          :form-validated="formValidated"
        />

        <NoteSection
          label="Time of Service"
          required
          :highlight-required="isTimeOfServiceIncomplete"
          :form-validated="formValidated"
        >
          <time-picker
            v-model="amnios_data.time_of_service"
            required
            :hours="hours"
            :minutes="minutes"
            default-time
          />
        </NoteSection>

        <NoteSection
          label="Ulcer duration prior to treatment"
        >
          <p class="mb-3">
            Please indicate the duration of time, in weeks, that this wound was
            present prior to first being treated by Skilled Wound Care. This value
            only needs to be entered once per wound, but it is optional — leave
            blank if unknown:
          </p>
          <v-row justify="center" align="center">
            <v-col cols="3" >
              <text-field
                v-model="amnio_duration_weeks"
                label="Weeks"
                mask="###"
                numeric
                clearable
                rules="numberRange(1,999)"
              />
            </v-col>
          </v-row>
        </NoteSection>

        <NoteSection
          label="Initial procedure performed for preparation of amniotic tissue graft placement"
        >
          <span>
            If an initial procedure was performed on this wound prior to the application of the amniotic tissue graft itself,
            indicate that here by choosing it from the list.
          </span>
          <auto-complete-box
            v-model="amnios_data.secondary_procedure"
            :items="woundProceduresForAmnio"
            label="Initial Procedure Performed"
            class="col-sm-12 mt-5"
            item-value="title"
            item-text="title"
            clearable
          ></auto-complete-box>
        </NoteSection>

        <NoteSection
          label="Treatment Photos"
          required
          :highlight-required="isTreatmentPhotosIncomplete"
          :form-validated="formValidated"
        >
          <div class="note-section col-sm-12">
            <v-expand-transition>
              <div v-if="!hasTreatmentPhotos">
                <alert color="error">
                  There should be at least one attachment of each type to pass validation.
                  <checkbox required />
                </alert>
              </div>
            </v-expand-transition>
          </div>

          <AmnioticAttachments
            :attachment-type.sync="attachmentType"
            attachment-type-id="3"
            :patient-attachments.sync="patientAttachments"
            :patient-id="patientId"
            :default-attachment-type="'amnio-meas'"
            :provided-buckets="buckets"
            :treatment-id="treatmentId"
          />
        </NoteSection>

        <NoteSection
          label="Wound thickness"
          required
          :highlight-required="isThicknessIncomplete"
          :form-validated="formValidated"
        >
          <p class="mb-3">
            Indicate if this is a partial thickness wound (shallow) or
            full thickness wound (extending through the dermis):
          </p>
          <radio-buttons
            v-model="amnios_data.thickness"
            :items="$store.getters['baseData/woundThickness']"
            required
            class="col-sm-6 mb-3"
          />
        </NoteSection>

        <NoteSection
          label="Diabetes management"
          required
          :highlight-required="isDiabetesManagementIncomplete"
          :form-validated="formValidated"
        >
          <radio-buttons
            v-model="amnios_data.diabetes"
            :items="$store.getters['baseData/diabetesManagement']"
            required
            columns="1"
            class="col-sm-5 mb-3"
          />
          <v-expand-transition>
            <span
              v-if="amnios_data.diabetes === 'Patient has diabetes'"
              class="mb-3"
            >
              <p>
                Explain what medical treatment is being performed to manage this
                patient's diabetes diagnosis. Keep in mind that the patient's list
                of medications will appear on the final note prior to this statement.
                Be sure to include information about how well the patient's blood
                glucose is being controlled, including a recent A1c level measurement
                if available.
              </p>
              <text-area
                v-model="amnios_data.diabetes_management"
                label="Diabetes management"
                required
                clear-warning
                counter="500"
                maxlength="500"
                class="mb-3"
              />
            </span>
          </v-expand-transition>
        </NoteSection>

        <NoteSection
          label="Tobacco cessation counseling"
          required
          :highlight-required="isTobaccoCessationIncomplete"
          :form-validated="formValidated"
        >
          <radio-buttons
            v-model="amnios_data.tobacco_cessation"
            :items="$store.getters['baseData/tobaccoCessation']"
            required
            columns="1"
            class="col-sm-7 mb-3"
          />
        </NoteSection>

        <NoteSection
          label="VLU duplex ultrasound"
        >
          <v-row class="mb-3" no-gutters align="start">
            <v-col cols="auto">
              <v-checkbox
                v-model="amnios_data.is_duplex_ultrasound_performed"
                :ripple="false"
                hide-details
                class="mt-0"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                Check this box to indicate that, while a VLU duplex ultrasound may be
                unavailable to be performed in-facility, a physical exam confirmed the presence
                of venous disease. This also classifies the wound as CEAP classification C6.
                <strong> This is optional.</strong>
              </p>
            </v-col>
          </v-row>
        </NoteSection>

        <NoteSection
          label="Absence of osteomyelitis"
          required
          :highlight-required="isOsteomyelitisIncomplete"
          :form-validated="formValidated"
        >
          <v-row class="mb-3" no-gutters align="start">
            <v-col cols="auto">
              <v-checkbox
                v-model="amnios_data.is_no_osteomyelitis"
                required
                :rules="[rules.mustBeTrue]"
                :ripple="false"
                hide-details
                class="mt-0"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                Check this box to indicate that the patient is not currently exhibiting
                osteomyelitis. This is a requirement for the application of an amniotic
                tissue graft.
              </p>
            </v-col>
          </v-row>
        </NoteSection>

        <NoteSection
          label="Adequate blood circulation"
          :required="isPadPvd"
          :highlight-required="isCirculationIncomplete"
          :form-validated="formValidated"
        >
          <v-row
            v-if="isPadPvd"
            class="mb-3"
            no-gutters
            align="start"
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="amnios_data.is_adequate_circulation"
                required
                :rules="[rules.mustBeTrue]"
                :ripple="false"
                hide-details
                class="mt-0"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                Check this box to indicate that you have ascertained that the patient is exhibiting
                adequate blood circulation. This includes measuring an ankle-brachial index/ABI
                of 0.6 or greater, as well as a toe pressure of at least 30 mmHg.
              </p>
            </v-col>
          </v-row>
          <v-row
            v-else
            class="my-3 justify-center align-center"
          >
            <p class="text-muted">
              [This section is only applicable to PAD/PVD etiologies.]
            </p>
          </v-row>
        </NoteSection>

        <NoteSection
          label="Wound site exudate"
          required
          :highlight-required="isExudateIncomplete"
          :form-validated="formValidated"
        >
          <v-row class="mb-3" no-gutters align="start">
            <v-col cols="auto">
              <v-checkbox
                v-model="amnios_data.is_no_exudate"
                required
                :rules="[rules.mustBeTrue]"
                :ripple="false"
                hide-details
                class="mt-0"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                Check this box to indicate explicitly that the wound site exhibits
                minimal exudate and that no signs of malodorous or infectious exudate
                are present. This is a requirement for the application of an amniotic
                tissue graft.
              </p>
            </v-col>
          </v-row>
        </NoteSection>

        <AmnioOperativeNote
          v-model="amnios_data"
          :wound-treatment-amnio-fixations.sync="fixations_data"
          :patient-id="patientId"
          :patient-attachments.sync="patientAttachments"
          :patient-amnio-consent="patientAmnioConsent"
          :form-validated="formValidated"
          @update:patientAmnioConsent="$emit('update:patientAmnioConsent', $event)"
        >
        </AmnioOperativeNote>

        <ProductInfo
          :treatment-id="treatmentId"
          :product-used.sync="amnios_data.product_used"
          :product-wasted.sync="amnios_data.product_wasted"
          :quantity-wasted.sync="amnios_data.quantity_wasted"
          :reason-wasted.sync="amnios_data.reason_wasted"
          :dim-length.sync="amnios_data.dim_length"
          :dim-width.sync="amnios_data.dim_width"
          :amnio-grafts.sync="grafts_data"
          :encounter-created-date="encounterCreatedDate"
          :form-validated="formValidated"
        />

        <v-card-actions class="sticky-bottom">
          <v-spacer></v-spacer>
          <!-- save -->
          <btn
            label="Save/Close"
            :icon="icons.mdiCheck"
            @click="save"
          ></btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import AmnioMedicalNecessity from '@/components/notes/wound-detail/treatments/sections/AmnioticSections/AmnioMedicalNecessity.vue'
import AmnioOperativeNote from '@/components/notes/wound-detail/treatments/sections/AmnioticSections/AmnioOperativeNote.vue'
import AmnioticAttachments from '@/components/notes/wound-detail/treatments/sections/AmnioticSections/AmnioticAttachments.vue'
import ProductInfo from '@/components/notes/wound-detail/treatments/sections/AmnioticSections/ProductInfo.vue'
import {
  mdiCheck,
  mdiUndoVariant,
} from '@mdi/js'
import { mapGetters } from 'vuex'

const amniosInitialData = {
  treatment_id: null,
  application_notes: null,
  diabetes: null,
  diabetes_management: null,
  dim_length: null,
  dim_width: null,
  dim_depth: null,
  is_adequate_circulation: false,
  is_duplex_ultrasound_performed: false,
  is_no_exudate: false,
  is_no_osteomyelitis: false,
  product_identification: null,
  product_used: null,
  quantity_used: null,
  quantity_wasted: null,
  reason_wasted: null,
  product_wasted: null,
  reason_required: null,
  secondary_procedure: null,
  thickness: null,
  time_of_service: null,
  tobacco_cessation: null,
}

export default {
  name: 'AmnioticTissueGraft',
  components: { AmnioMedicalNecessity, AmnioOperativeNote, AmnioticAttachments, ProductInfo },
  props: {
    amnio: {
      type: Object,
      default: null,
    },
    amnioGrafts: {
      type: Array,
      default: () => [],
    },
    fixations: {
      type: Array,
      default: () => [],
    },
    woundId: {
      type: String,
      default: null,
    },
    treatmentId: {
      type: String,
      default: null,
    },
    encounterVisitDate: {
      type: [String, Date],
      default: null,
    },
    encounterCreatedDate: {
      type: [String, Date],
      default: null,
    },
    amnioDurationWeeks: {
      type: [Number, String],
      default: null,
    },
    isPadPvd: {
      type: Boolean,
      default: null,
    },
    woundCurrentArea: {
      type: [Number, String],
      default: null,
    },
    modalState: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: null,
    },
    patientAttachments: {
      type: Array,
      default: () => [],
    },
    patientAmnioConsent: {
      type: String,
      default: '',
    },
    amnioFormValid: {
      type: Boolean,
      default: false,
    },
    closed: {
      type: Boolean,
      default: false,
    },
    // Form validation state passed down
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      amnios_data: {
        ...amniosInitialData,
        ...this.$lodash.cloneDeep(this.amnio || {}),
        treatment_id: this.treatmentId,
      },
      grafts_data: this.amnioGrafts,
      fixations_data: this.fixations,
      amnio_duration_weeks: this.amnioDurationWeeks,
      icons: {
        mdiCheck, mdiUndoVariant,
      },
      rules: {
        mustBeTrue: value => value === true || 'This field must be checked',
      },
      hours: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
      minutes: this.getMinutes(),
      buckets: [],
      attachmentType: 'images',
    }
  },
  computed: {
    ...mapGetters('baseData', ['woundProcedures']),
    woundProceduresForAmnio() {
      // Exclude Amniotic Tissue per TXP-251
      return this.woundProcedures.filter(wp => !wp.title.toLowerCase().includes('amniotic tissue')
        && wp.id !== 178)
    },
    hasTreatmentPhotos() {
      const requiredTypes = ['amnio-packaging', 'amnio-meas', 'amnio-graft']

      return requiredTypes.every(type => this.patientAttachments?.some(att => att.attachment_type === type
        && att.treatment_id === this.treatmentId))
    },
    hasAmnioPackagingPhoto() {
      return this.patientAttachments.some(att => att.attachment_type === 'amnio-packaging' && att.treatment_id === this.treatmentId)
    },
    hasAmnioMeasPhoto() {
      return this.patientAttachments.some(att => att.attachment_type === 'amnio-meas' && att.treatment_id === this.treatmentId)
    },
    hasAmnioGraftPhoto() {
      return this.patientAttachments.some(att => att.attachment_type === 'amnio-graft' && att.treatment_id === this.treatmentId)
    },
    hasAmnioConsentPhoto() {
      return this.patientAttachments?.some(att => att.attachment_type === 'amnio-consent')
    },
    isProductWasted() {
      return this.amnios_data.product_wasted === 'Some product was wasted.'
    },

    // Evaluates incomplete states for section highlights
    isTimeOfServiceIncomplete() {
      return !this.amnios_data.time_of_service
    },
    isTreatmentPhotosIncomplete() {
      return !this.hasTreatmentPhotos
    },
    isThicknessIncomplete() {
      return !this.amnios_data.thickness
    },
    isDiabetesManagementIncomplete() {
      if (!this.amnios_data.diabetes) return true
      if (this.amnios_data.diabetes === 'Patient has diabetes' && (!this.amnios_data.diabetes_management || !this.amnios_data.diabetes_management.trim())) {
        return true
      }
      return false
    },
    isTobaccoCessationIncomplete() {
      return !this.amnios_data.tobacco_cessation
    },
    isOsteomyelitisIncomplete() {
      return !this.amnios_data.is_no_osteomyelitis
    },
    isCirculationIncomplete() {
      if (!this.isPadPvd) return false
      return !this.amnios_data.is_adequate_circulation
    },
    isExudateIncomplete() {
      return !this.amnios_data.is_no_exudate
    },
  },
  watch: {
    modalState() {
      const modalTitle = this.$refs.amnioTitle
      if (modalTitle) modalTitle.scrollIntoView(true)
    },
  },
  created() {
    this.buckets = this.getBuckets()
  },
  mounted() {
  },
  methods: {
    getMinutes() {
      return Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))
    },
    async save() {
      this.$emit('update:wound_treatments_amnios', this.amnios_data)
      this.$emit('update:amnio_grafts', this.grafts_data)
      this.$emit('update:wound_treatment_amnio_fixations', this.fixations_data)
      this.$emit('update:amnio-duration-weeks', this.amnio_duration_weeks)
      this.$emit('update:modal-state', false)
      this.$emit('update:patientAmnioConsent', this.patientAmnioConsent)
      this.$emit('update:closed', true)
      this.$emit('update:amnio-form-valid', await this.validate())
      this.$emit('update:closed', false)
    },
    getBuckets() {
      const amnioPackagingList = this.$store.getters['baseData/amnioticBucketTypeFromValue']('amnio-packaging')
      const amnioMeasList = this.$store.getters['baseData/amnioticBucketTypeFromValue']('amnio-meas')
      const amnioGraftList = this.$store.getters['baseData/amnioticBucketTypeFromValue']('amnio-graft')

      return [
        amnioMeasList,
        amnioGraftList,
        amnioPackagingList,
      ]
    },
    async validate() {
      // Fixes issue when validating and not touching the amnio button, waits for dom update cycle to finish
      await this.$nextTick()

      return this.$refs.formAmnio?.validate?.()
    },
    getInvalidFields() {
      const invalids = []
      const requiredFields = this.getRequiredFieldMapping()

      Object.keys(requiredFields).forEach(key => {
        const msg = requiredFields[key]

        if (key === 'diabetes_management') {
          if (this.amnios_data.diabetes === 'Patient has diabetes' && !this.amnios_data.diabetes_management) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'quantity_wasted') {
          if (this.isProductWasted && !this.amnios_data.quantity_wasted) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'reason_wasted') {
          if (this.isProductWasted && !this.amnios_data.reason_wasted) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'consent_obtained_from') {
          if (!this.patientAmnioConsent) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'consent_photo') {
          if (!this.hasAmnioConsentPhoto) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'is_adequate_circulation') {
          if (this.isPadPvd && !this.amnios_data.is_adequate_circulation) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'amnio_graft') {
          if (!this.hasAmnioGraftPhoto) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'amnio_meas') {
          if (!this.hasAmnioMeasPhoto) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'amnio_packaging') {
          if (!this.hasAmnioPackagingPhoto) {
            invalids.push(msg)
          }
          return
        }

        if (key === 'fixations') {
          if (!this.fixations_data || !this.fixations_data.length) {
            invalids.push(msg)
          }
          return
        }

        if ((key === 'graft_length' || key === 'graft_width' || key === 'graft_qty') && !invalids.includes(msg)) {
          const hasInvalid = this.grafts_data.some(g => {
            const matchesId = g.treatment_id === this.treatmentId

            return matchesId && (
              g.graft_length < 0.1 || g.graft_length > 999 ||
              g.graft_width  < 0.1 || g.graft_width  > 999 ||
              g.graft_qty    < 0.1 || g.graft_qty    > 999
            )
          })

          if (hasInvalid) {
            invalids.push(msg)
          }

          return
        }

        if (this.amnios_data.hasOwnProperty(key) && !this.amnios_data[key]) {
          invalids.push(msg)
        }
      })

      return invalids
    },
    getRequiredFieldMapping() {
      return {
        reason_required: 'Reason for medical necessity has not been set.',
        time_of_service: 'Time of service has not been set.',
        amnio_meas: 'A photo showing the wound and its dimensions is not present.',
        amnio_graft: 'A photo showing the wound with the amniotic tissue graft product applied is not present.',
        amnio_packaging: 'A photo showing the amniotic tissue graft product packaging is not present.',
        thickness: 'Wound thickness has not been chosen.',
        diabetes: 'Diabetes management diabetes indication has not been set.',
        diabetes_management: 'Diabetes management description has not been set.',
        tobacco_cessation: 'Tobacco cessation counseling selection has not been made.',
        is_no_osteomyelitis: 'You must indicate that the patient does not exhibit osteomyelitis in order for the treatment to be accepted.',
        is_adequate_circulation: 'As this wound has a PAD/PVD etiology, you must indicate that the patient exhibits adequate blood circulation for the treatment to be accepted.',
        is_no_exudate: 'You must indicate that the wound site exhibits minimal exudate for the treatment to be accepted.',
        consent_photo: 'Operative Note: A photo of the consent document is not present.',
        consent_obtained_from: 'Operative Note: Consent source has not been set.',
        dim_length: 'Operative Note: Wound length has not been set.',
        dim_width: 'Operative Note: Wound width has not been set.',
        dim_depth: 'Operative Note: Wound depth has not been set.',
        fixations: 'Operative Note: Indication of fixation has not been set.',
        application_notes: 'Operative Note: Application Notes has not been set.',
        product_used: 'Manufacturer and product name of the amniotic tissue graft has not been entered.',
        graft_length: 'Product quantity information invalid: missing or incorrect value.',
        graft_width: 'Product quantity information invalid: missing or incorrect value.',
        graft_qty: 'Product quantity information invalid: missing or incorrect value.',
        product_wasted: 'Indication of if product was wasted has not been set.',
        quantity_wasted: 'Product quantity wasted has not been set.',
        reason_wasted: 'Reason why product wasted has not been set.',
      }
    },
  },
}
</script>
